import { ArchBaseService } from "@/common/api.service";

/**
 * AI Workspace settings, behind `ai.config.manage`.
 *
 * Deliberately a separate service from `aiWorkspaceService` rather than more methods on
 * it: these routes are gated on a different, narrower permission, and every one of them
 * is a money decision — a token allowance, a spend ceiling, a model choice, or the kill
 * switch. Keeping them apart means a chat screen can never accidentally import a write
 * that costs money, and mirrors the backend's own split (`/ai/admin` vs `/ai`).
 *
 * `ArchBaseService.init()` runs before every request for the same reason it does in
 * `aiWorkspaceService`: every service in `common/api.service.js` writes the same shared
 * `axios.defaults.baseURL`, so whichever `init()` ran most recently owns it globally.
 */
export const AiAdminService = {
  /**
   * The single settings row.
   *
   * `chatEnabled` and `chatEnabledByEnvironment` arrive separately and must stay that
   * way in the interface. Either being off means off, so an administrator switching
   * `chatEnabled` on while the environment backstop is false needs to see why the
   * workspace stayed dark rather than concluding the screen is broken.
   *
   * @returns {Promise} { defaultModelId, chatEnabled, chatEnabledByEnvironment,
   *   maxContextTurns, maxContextTokens, maxOutputTokens, summaryTriggerTokens,
   *   orgSpendCeilingMicros, spendAlertThresholds, webSearchEnabled, updatedAt }
   */
  getConfig() {
    ArchBaseService.init();
    return ArchBaseService.get("ai/admin/config");
  },

  /**
   * Apply settings changes.
   *
   * A PATCH carrying only what changed, never the whole form. The backend is explicit
   * that this is the point of the verb: a form that round-trips every field is how a
   * token budget someone else raised this morning gets silently reverted by an
   * administrator who only came here to flip the kill switch.
   *
   * @param {Object} changes - only the fields being changed
   * @returns {Promise} the updated config view
   */
  updateConfig(changes) {
    ArchBaseService.init();
    return ArchBaseService.patch("ai/admin/config", changes);
  },

  /**
   * The model allowlist.
   *
   * `modelId` is the exact provider identifier and is what gets sent to OpenAI;
   * `displayName` is for people. Disabled rows are returned too — the current
   * `defaultModelId` may point at one, and a select that silently dropped it would show
   * a blank where the live model should be.
   *
   * @returns {Promise} [{ modelId, displayName, isEnabled, notes }]
   */
  getModels() {
    ArchBaseService.init();
    return ArchBaseService.get("ai/admin/models");
  },

  /**
   * Quota profiles with their limits and the roles carrying them.
   *
   * `effectiveMaxResponseTokens` is the number that will actually be used — the lesser
   * of the profile's own limit and the workspace-wide `maxOutputTokens`. It is reported
   * alongside rather than instead of `maxResponseTokens` so a profile set to 8,192 and
   * silently getting 4,096 can be seen for what it is.
   *
   * @returns {Promise} [{ id, name, requestsPerMinute, monthlyTokenAllowance,
   *   maxMessageChars, maxResponseTokens, effectiveMaxResponseTokens,
   *   generationTimeoutSeconds, monthlyWebSearchCalls, isActive, assignedRoles }]
   */
  getQuotaProfiles() {
    ArchBaseService.init();
    return ArchBaseService.get("ai/admin/quota-profiles");
  },

  /**
   * Create a profile.
   *
   * Names are unique and the seed script matches on them, so a duplicate is rejected
   * with a 409 rather than quietly creating a second, orphaned row.
   *
   * @param {Object} profile
   * @returns {Promise} the created profile view
   */
  createQuotaProfile(profile) {
    ArchBaseService.init();
    return ArchBaseService.post("ai/admin/quota-profiles", profile);
  },

  /**
   * Tune a profile's limits, or retire it with `isActive: false`.
   *
   * Deactivating denies every role carrying the profile exactly as detaching it would,
   * which is why the assigned roles are shown on the same row as the switch.
   *
   * @param {Number} id
   * @param {Object} changes - only the fields being changed
   * @returns {Promise} the updated profile view
   */
  updateQuotaProfile(id, changes) {
    ArchBaseService.init();
    return ArchBaseService.patch(`ai/admin/quota-profiles/${id}`, changes);
  },

  /**
   * Every role and the profile it carries, including the roles carrying none.
   *
   * The empty ones are the reason this endpoint exists rather than a list of
   * assignments: a role with no profile is denied, and that denial is otherwise
   * invisible until someone in that role reports the workspace does nothing.
   *
   * @returns {Promise} [{ roleId, roleName, quotaProfileId, quotaProfileName }]
   */
  getRoleAssignments() {
    ArchBaseService.init();
    return ArchBaseService.get("ai/admin/roles");
  },

  /**
   * Grant or remove `ai.chat.use` for a role.
   *
   * Writes the single row rather than replacing the role's whole permission set, which
   * is what `POST /permission/update` does — that endpoint deletes every permission a
   * role holds and re-inserts what it is sent, so toggling one entry through it means a
   * read-modify-write that can drop whatever another screen added in between.
   *
   * @param {String} roleId
   * @param {Boolean} enabled
   * @returns {Promise} { updated, enabled }
   */
  setRoleChatAccess(roleId, enabled) {
    ArchBaseService.init();
    return ArchBaseService.patch(`ai/admin/roles/${roleId}/chat-access`, { enabled });
  },

  /**
   * Attach a profile to a role, or detach it with null.
   *
   * This is the pilot control. Detaching denies the role without touching its
   * permissions — the difference between "not in the pilot yet" and "not allowed to use
   * this", which are two states an administrator needs to be able to express separately.
   *
   * @param {String} roleId
   * @param {Number|null} quotaProfileId
   * @returns {Promise} { updated }
   */
  setRoleQuotaProfile(roleId, quotaProfileId) {
    ArchBaseService.init();
    return ArchBaseService.patch(`ai/admin/roles/${roleId}/quota-profile`, {
      quotaProfileId,
    });
  },
};

export default AiAdminService;
